import { Platform } from 'react-native';

/**
 * CORE-2091 — «вкладка сейчас перезагрузится, а не закроется».
 *
 * `pagehide` одинаков для закрытия вкладки и для перезагрузки. Из-за этого
 * кадр ухода при закрытии (CORE-461, `api.leaveRoomBeacon`) уходил и на F5:
 * сервер снимал участника, через полсекунды новая страница входила снова, и у
 * остальных строка мигала «вышел → вошёл» (QA 05.11.2026, 594–738 мс), а
 * `/join` новой страницы гонялся с этим уходом (CORE-942).
 *
 * Перезагрузку, о которой страница знает заранее, можно отличить:
 *   - клавиши перезагрузки (F5, Ctrl/Cmd+R, с Shift — тоже) приходят странице
 *     keydown-ом раньше, чем браузер начнёт перезагрузку;
 *   - перезагрузку из нашего же кода — `markReloadIntent()` перед
 *     `window.location.reload()`.
 * Кнопку браузера и «потянуть вниз» на телефоне страница не видит — там
 * остаётся прежнее поведение (кадр ухода уходит).
 *
 * Намерение живёт недолго и гаснет, как только человек продолжил работать со
 * страницей (отменил «Перезагрузить сайт?» и кликнул / нажал другую клавишу):
 * иначе последующее настоящее закрытие вкладки прошло бы без кадра ухода.
 */
export const RELOAD_INTENT_TTL_MS = 30_000;

let markedAt: number | null = null;

export function markReloadIntent(now: number = Date.now()): void {
    markedAt = now;
}

export function clearReloadIntent(): void {
    markedAt = null;
}

export function isReloadIntended(now: number = Date.now()): boolean {
    return markedAt !== null && now - markedAt >= 0 && now - markedAt <= RELOAD_INTENT_TTL_MS;
}

interface KeyLike {
    key?: string;
    code?: string;
    ctrlKey?: boolean;
    metaKey?: boolean;
    altKey?: boolean;
}

/** F5 (с любыми модификаторами) или Ctrl/Cmd+R (с Shift тоже), без Alt. */
export function isReloadShortcut(e: KeyLike): boolean {
    if (e.key === 'F5' || e.code === 'F5') return true;
    if (e.altKey) return false;
    if (!(e.ctrlKey || e.metaKey)) return false;
    return e.code === 'KeyR' || e.key === 'r' || e.key === 'R';
}

/** Модификатор сам по себе — часть сочетания, а не «продолжил работать». */
function isBareModifier(e: KeyLike): boolean {
    return e.key === 'Control' || e.key === 'Meta' || e.key === 'Shift' || e.key === 'Alt';
}

/**
 * Слушать клавиши перезагрузки на вебе. Возвращает отписку; на нативе — no-op.
 * Слушатели в фазе захвата: поле ввода с `stopPropagation` не должно прятать F5.
 */
export function installReloadIntentListeners(): () => void {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return () => {};
    const onKeyDown = (e: KeyboardEvent) => {
        if (isReloadShortcut(e)) markReloadIntent();
        else if (!isBareModifier(e)) clearReloadIntent();
    };
    const onPointerDown = () => clearReloadIntent();
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
        window.removeEventListener('keydown', onKeyDown, true);
        window.removeEventListener('pointerdown', onPointerDown, true);
    };
}
